/**
 * SafeIntersections AppConfig — composed from the per-slot modules in this
 * directory.
 */

import type { AppConfig } from '#lib/apps/types.js';
import {
	ESafeIntersectionsModule,
	EIdentify,
	EPrioritize,
	type ISafeIntersectionsSystemLayers
} from './interfaces';
import LogoSafeIntersections from '#lib/assets/logos/safeintersections.svg?raw';

import { modules, SI_DATASET, INTERVENTIONS_MODULE_ID } from './layers';
import SummaryDataContent from './widgets/SummaryDataContent.svelte';
import { dashboard, method } from './header';
import { loadSystem, loadSummary } from './loaders';
import { parseLegendLayers } from './legendHelpers';
import { tableColumnsByTab, mapPriorityDescription } from './columns';
import AttributesPanel from './AttributesPanel/AttributesPanel.svelte';
import Countermeasures from './Countermeasures/Countermeasures.svelte';
import IdentifyLegend from '../legends/IdentifyLegend.svelte';
import PrioritizeLegend from '../legends/PrioritizeLegend.svelte';
import InterventionsLegend from './legends/InterventionsLegend.svelte';
import type { LegendSection } from '#lib/components/map/legend/types.js';
import {
	ECrashes,
	ECrashesModule,
	type ICrashesSystemLayers
} from '#lib/apps/safesystem/context/interfaces.js';
import { bboxTuple } from '#lib/common/layers.js';
import { mapPriorityDisplayLabel, priorityLabel } from '../priority';

type SafeIntersectionsSystem = ISafeIntersectionsSystemLayers & ICrashesSystemLayers;

const legendSections: LegendSection[] = [
	{
		label: 'Identify',
		layers: [
			{
				moduleId: ESafeIntersectionsModule.Identify,
				layerId: EIdentify.Identify,
				label: 'Intersections'
			}
		]
	},
	{
		label: 'Prioritize',
		layers: [
			{
				moduleId: ESafeIntersectionsModule.Prioritize,
				layerId: EPrioritize.Prioritize,
				label: 'Intersections',
				symbology: { formatLabel: (key) => priorityLabel(mapPriorityDescription, key) }
			}
		]
	},
	{
		label: 'Crashes',
		layers: [
			{
				moduleId: ECrashesModule.Crashes,
				layerId: ECrashes.CrashesCircle,
				label: 'Fatal crash'
			}
		]
	},
	{
		label: 'Uploaded crash data',
		layers: [
			{
				moduleId: ECrashesModule.Crashes,
				layerId: ECrashes.UploadedCrashes,
				label: 'Uploaded crash'
			}
		]
	}
];

export const safeintersectionsConfig: AppConfig<SafeIntersectionsSystem> = {
	id: 'safeintersections',
	name: 'SafeIntersections Professional',
	basePath: '/safeintersections',
	head: {
		title: 'Abley SafeIntersections Professional',
		description: 'Proactive, data-driven tools to identify road safety risks.',
		keywords: 'SafeSystem, SafeIntersections, Abley Ltd.'
	},
	logo: LogoSafeIntersections,
	basemap: 'no-roads',
	version: '0.1.1',
	// The crashes overlay is served alongside SafeIntersections; it doesn't gate availability.
	datasets: [SI_DATASET],

	loaders: {
		system: loadSystem,
		summary: loadSummary
	},

	modules,
	legends: [
		{
			moduleId: ESafeIntersectionsModule.Identify,
			component: IdentifyLegend,
			props: { heading: 'Intersections' }
		},
		{
			moduleId: ESafeIntersectionsModule.Prioritize,
			component: PrioritizeLegend,
			props: {
				heading: 'Intersections',
				labels: mapPriorityDisplayLabel
			}
		},
		{ moduleId: INTERVENTIONS_MODULE_ID, component: InterventionsLegend }
	],

	parseLegendLayers,
	legendSections,

	map: {
		getDataBounds: (systemData) => {
			const bbox =
				systemData?.[ESafeIntersectionsModule.Prioritize]?.[EPrioritize.Prioritize]?.bbox;
			return bbox && bboxTuple(bbox);
		},
		getLabelsAnchorLayerId: (systemData) =>
			systemData?.[ECrashesModule.Crashes]?.[ECrashes.CrashesCircle]?.layer?.id,
		customCrashesSourceId: `${ECrashes.UploadedCrashes}-source`
	},

	table: {
		tabs: [
			{
				id: 'Identify',
				label: 'Identify',
				rowLabel: 'intersection',
				columns: tableColumnsByTab.Identify,
				getLayerId: (systemData) =>
					systemData?.[ESafeIntersectionsModule.Identify]?.[EIdentify.Identify]?.layer?.id
			},
			{
				id: 'Prioritize',
				label: 'Prioritize',
				rowLabel: 'intersection',
				columns: tableColumnsByTab.Prioritize,
				getLayerId: (systemData) =>
					systemData?.[ESafeIntersectionsModule.Prioritize]?.[EPrioritize.Prioritize]?.layer?.id
			}
		],
		defaultTabId: 'Identify',
		filterPlaceholder: 'Filter table by road/county'
	},

	// SafeIntersections has no usefully filterable attributes — the map's
	// DataFilter control is hidden for filter-less apps (see Map.svelte).
	filters: [],

	dashboard: {
		widgets: [{ id: 'summary', component: SummaryDataContent }],
		backgroundImage: '/landing_bg.webp'
	},

	header: {
		dashboard,
		method
	},

	attributes: {
		panel: AttributesPanel,
		countermeasures: Countermeasures,
		issueReporting: true,
		// Wider panel — the interventions calculator's Features table needs the
		// room (the React source used 820px too).
		panelWidth: 820
	}
};
